import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useState, useEffect } from "react";
import LoginPage from "./pages/LoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import { getUserRole, getUserId } from "./utils/Auth";
import StocPage from "./components/StocPage";
import CategoryProductsPage from "./pages/CategoryProductsPage";
import Sidebar from "./components/Sidebar";
import ClientiiMeiList from "./components/ClientiiMeiList";
import OrdersList from "./components/OrdersList";
import CustomerList from "./components/CustomerList";
import AllOrdersList from "./components/AllOrdersList";
import UserList from "./components/UserList";
import OfertaList from "./components/OfertaList";
import ReactModal from "react-modal";
import AllOffersList from "./components/AllOffersList";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import CustomerStatistics from "./components/CustomerStatistics";
import ChangePasswordPage from "./components/ChangePasswordPage";
import API_BASE_URL from "./config";
import ProductSlabsPage from "./components/ProductSlabsPage";
import ChatBox from "./components/ChatBox";
import TasksBoard from "./components/TasksBoard";
import RemindersPage from "./components/RemindersPage";
import SalesStatistics from "./components/SalesStatistics";
import TopProducts from "./components/TopProducts";
import AuditLogPage from "./components/AuditLogPage";
import AgentAnalyticsPage from "./components/AgentAnalyticsPage";

ReactModal.setAppElement("#root");

function App() {
  const [role, setRole] = useState(null);
  const [userName, setUserName] = useState("");
  const [view, setView] = useState("dashboard");
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem("darkMode");
    if (savedTheme) {
      setDarkMode(JSON.parse(savedTheme));
    }
  }, []);

  const toggleTheme = () => {
    setDarkMode((prevMode) => {
      localStorage.setItem("darkMode", JSON.stringify(!prevMode));
      return !prevMode;
    });
  };

  useEffect(() => {
    const fetchUserData = async () => {
      const userId = getUserId();
      if (!userId) return;

      try {
        const token = localStorage.getItem("token");

        const response = await fetch(`${API_BASE_URL}/api/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error("Hiba a felhasználó adatainak betöltésekor.");
        }

        const userData = await response.json();
        setUserName(userData.name);
        setRole(getUserRole());
      } catch (err) {
        console.error("⚠️ Hiba történt:", err);
      }
    };

    fetchUserData();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    setRole(null);
    setUserName("");
    window.location.href = "/";
  };

  return (
    <ThemeProvider>
      {" "}
      <Router>
        <MainApp
          role={role}
          userName={userName}
          setRole={setRole}
          setUserName={setUserName}
          handleLogout={handleLogout}
        />
      </Router>
    </ThemeProvider>
  );
}

function MainApp({ role, userName, setRole, setUserName, handleLogout }) {
  const location = useLocation();
  const { darkMode } = useTheme();

  const backgroundClass = darkMode
    ? location.pathname.startsWith("/stoc") ||
      location.pathname.startsWith("/category")
      ? "bg-gray-900"
      : "bg-gray-900"
    : location.pathname.startsWith("/stoc") ||
      location.pathname.startsWith("/category")
      ? "bg-gray-200"
      : "bg-gray-200";

  return (
    <div className={`h-screen flex flex-col ${backgroundClass}`}>
      <div
        className={`w-full text-xl font-bold mx-auto h-16 flex items-center justify-center px-4 py-4 
        ${darkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-black"}`}
        style={{
          backgroundImage: "url('/productimages/MARMURĂ ARABESCATO.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="text-xl font-bold mx-auto">
          <img
            src="/images/marmorexlogo_transparent.png"
            alt="Marmorex CRM Logo"
            className="h-10"
          />
        </div>

        {role && (
          <button
            onClick={handleLogout}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-3xl absolute right-6"
          >
            Deconectare
          </button>
        )}
      </div>

      {role ? (
        <div className="flex-1 flex">
          <Sidebar
            role={role}
            userName={userName}
            setView={() => { }}
            view=""
            fetchCustomers={() => { }}
            fetchUsers={() => { }}
            fetchMyCustomers={() => { }}
            fetchOrders={() => { }}
            fetchAllOrders={() => { }}
          />

          <div className={`flex-1 p-6 ml-64 ${backgroundClass}`}>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" />} />
              <Route path="/dashboard" element={<AdminDashboard />} />
              <Route path="/stoc" element={<StocPage />} />
              <Route
                path="/category/:categoryId"
                element={<CategoryProductsPage />}
              />
              <Route path="/dashboard/users" element={<UserList />} />
              <Route
                path="/dashboard/myCustomers"
                element={<ClientiiMeiList />}
              />
              <Route path="/dashboard/customers" element={<CustomerList />} />
              <Route path="/dashboard/tasks" element={<TasksBoard />} />
              <Route path="/dashboard/reminders" element={<RemindersPage />} />
              <Route path="/dashboard/orders" element={<OrdersList />} />
              <Route path="/dashboard/allOrders" element={<AllOrdersList />} />
              <Route path="/dashboard/offers" element={<OfertaList />} />
              <Route path="/dashboard/allOffers" element={<AllOffersList />} />
              <Route
                path="/dashboard/customer-stats"
                element={<CustomerStatistics />}
              />
              <Route
                path="/products/:productId/slabs"
                element={<ProductSlabsPage />}
              />
              <Route path="/dashboard/sales-stats" element={<SalesStatistics />} />
              <Route path="/dashboard/top-products" element={<TopProducts />} />
              <Route path="/dashboard/audit-logs" element={<AuditLogPage />} />
              <Route path="/dashboard/agent/:agentId" element={<AgentAnalyticsPage />} />

              <Route path="/change-password" element={<ChangePasswordPage />} />

              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
          </div>
          <ChatBox />
        </div>
      ) : (
        <Routes>
          <Route
            path="/"
            element={<LoginPage setRole={setRole} setUserName={setUserName} />}
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      )}
    </div>
  );
}
export default App;
